/**
 * BlueprintEditorPage
 *
 * Three-column layout for editing story blueprints:
 * - Left: Blueprint list + Segments list (ordered)
 * - Center: Selected segment details with stage position
 * - Right: Petal picker for adding petals to segments
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import {
  Blueprint,
  BlueprintSegment,
  PetalDto,
  FlowerKey,
  StagePosition,
  EventType,
  BlueprintGoal,
  CreateBlueprintRequest,
  CreateSegmentRequest,
  useGetBlueprintsQuery,
  useGetBlueprintQuery,
  useCreateBlueprintMutation,
  useUpdateBlueprintMutation,
  useDeleteBlueprintMutation,
  useAddSegmentMutation,
  useUpdateSegmentMutation,
  useDeleteSegmentMutation,
  useReorderSegmentsMutation,
  useAttachPetalsToSegmentMutation,
  useGetPetalsQuery,
  FLOWER_LABELS,
  BOUQUET_LABELS,
  PETAL_TYPE_LABELS,
  EVENT_TYPE_LABELS,
  GOAL_LABELS,
  STAGE_POSITION_LABELS,
  STAGE_POSITION_ICONS,
} from '../store/api/blueprintsApi';

export function BlueprintEditorPage() {
  const { blueprintId } = useParams<{ blueprintId?: string }>();
  const navigate = useNavigate();

  // State
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(null);
  const [showCreateBlueprint, setShowCreateBlueprint] = useState(false);
  const [showAddSegment, setShowAddSegment] = useState(false);

  // RTK Query hooks
  const { data: blueprints = [], isLoading: blueprintsLoading } = useGetBlueprintsQuery();
  const { data: currentBlueprint, isLoading: blueprintLoading } = useGetBlueprintQuery(
    blueprintId || '',
    { skip: !blueprintId }
  );
  const { data: petals = [] } = useGetPetalsQuery();

  // Mutations
  const [createBlueprint, { isLoading: isCreating }] = useCreateBlueprintMutation();
  const [_updateBlueprint] = useUpdateBlueprintMutation();
  const [deleteBlueprint] = useDeleteBlueprintMutation();
  const [addSegment] = useAddSegmentMutation();
  const [updateSegment] = useUpdateSegmentMutation();
  const [deleteSegment] = useDeleteSegmentMutation();
  const [reorderSegments] = useReorderSegmentsMutation();
  const [attachPetals] = useAttachPetalsToSegmentMutation();

  // Get selected segment
  const selectedSegment = currentBlueprint?.segments.find((s) => s.id === selectedSegmentId);

  // Auto-select first segment when blueprint loads
  useEffect(() => {
    if (currentBlueprint?.segments.length && !selectedSegmentId) {
      setSelectedSegmentId(currentBlueprint.segments[0].id);
    }
  }, [currentBlueprint, selectedSegmentId]);

  // Reset selection when changing blueprints
  useEffect(() => {
    setSelectedSegmentId(null);
  }, [blueprintId]);

  // Handle blueprint creation
  const handleCreateBlueprint = async (data: CreateBlueprintRequest) => {
    try {
      const result = await createBlueprint(data).unwrap();
      navigate(`/blueprints/${result.id}`);
      setShowCreateBlueprint(false);
    } catch (err) {
      console.error('Failed to create blueprint:', err);
    }
  };

  // Handle blueprint deletion
  const handleDeleteBlueprint = async (id: string) => {
    if (!confirm('Delete this blueprint?')) return;
    try {
      await deleteBlueprint(id).unwrap();
      if (blueprintId === id) {
        navigate('/blueprints');
      }
    } catch (err) {
      console.error('Failed to delete blueprint:', err);
    }
  };

  // Handle segment addition
  const handleAddSegment = async (data: CreateSegmentRequest) => {
    if (!blueprintId) return;
    try {
      const result = await addSegment({ blueprintId, data }).unwrap();
      setSelectedSegmentId(result.id);
      setShowAddSegment(false);
    } catch (err) {
      console.error('Failed to add segment:', err);
    }
  };

  // Handle segment deletion
  const handleDeleteSegment = async (segmentId: string) => {
    if (!blueprintId || !confirm('Delete this segment?')) return;
    try {
      await deleteSegment({ segmentId, blueprintId }).unwrap();
      if (selectedSegmentId === segmentId) {
        setSelectedSegmentId(null);
      }
    } catch (err) {
      console.error('Failed to delete segment:', err);
    }
  };

  // Handle stage position change
  const handleStagePositionChange = async (segmentId: string, stagePosition: StagePosition) => {
    try {
      await updateSegment({ segmentId, data: { stagePosition } }).unwrap();
    } catch (err) {
      console.error('Failed to update stage position:', err);
    }
  };

  // Handle petal attachment toggle
  const handleTogglePetal = async (petalId: string) => {
    if (!selectedSegment) return;
    const currentPetalIds = selectedSegment.petals.map((p) => p.id);
    const newPetalIds = currentPetalIds.includes(petalId)
      ? currentPetalIds.filter((id) => id !== petalId)
      : [...currentPetalIds, petalId];

    try {
      await attachPetals({ segmentId: selectedSegment.id, data: { petalIds: newPetalIds } }).unwrap();
    } catch (err) {
      console.error('Failed to update petals:', err);
    }
  };

  // Handle segment reorder (move up/down)
  const handleMoveSegment = async (segmentId: string, direction: 'up' | 'down') => {
    if (!currentBlueprint || !blueprintId) return;
    const segments = [...currentBlueprint.segments].sort((a, b) => a.orderIndex - b.orderIndex);
    const idx = segments.findIndex((s) => s.id === segmentId);
    if (idx === -1) return;
    if (direction === 'up' && idx === 0) return;
    if (direction === 'down' && idx === segments.length - 1) return;

    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    const newOrder = segments.map((s, i) => {
      if (i === idx) return { id: s.id, orderIndex: swapIdx };
      if (i === swapIdx) return { id: s.id, orderIndex: idx };
      return { id: s.id, orderIndex: i };
    });

    try {
      await reorderSegments({ blueprintId, data: { segmentOrder: newOrder } }).unwrap();
    } catch (err) {
      console.error('Failed to reorder segments:', err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Story Blueprints</h1>
          <p className="mt-1 text-gray-500">
            Build structured outlines for your presentations and conversations
          </p>
        </div>
        <button
          onClick={() => setShowCreateBlueprint(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Blueprint
        </button>
      </div>

      {/* Main content - three column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left column - Blueprint & Segments list */}
        <div className="lg:col-span-3">
          {/* Blueprints list */}
          <div className="card mb-4">
            <div className="card-header">
              <h2 className="font-semibold text-gray-900">Blueprints</h2>
            </div>
            <div className="card-body">
              <BlueprintsList
                blueprints={blueprints}
                selectedId={blueprintId}
                onSelect={(id) => navigate(`/blueprints/${id}`)}
                onDelete={handleDeleteBlueprint}
                isLoading={blueprintsLoading}
              />
            </div>
          </div>

          {/* Segments list (when blueprint selected) */}
          {currentBlueprint && (
            <div className="card">
              <div className="card-header flex items-center justify-between">
                <h2 className="font-semibold text-gray-900">Segments</h2>
                <button
                  onClick={() => setShowAddSegment(true)}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  + Add
                </button>
              </div>
              <div className="card-body">
                <SegmentsList
                  segments={currentBlueprint.segments}
                  selectedId={selectedSegmentId}
                  onSelect={setSelectedSegmentId}
                  onDelete={handleDeleteSegment}
                  onMove={handleMoveSegment}
                  isLoading={blueprintLoading}
                />
              </div>
            </div>
          )}
        </div>

        {/* Center column - Segment details + Stage position */}
        <div className="lg:col-span-5">
          {selectedSegment ? (
            <div className="card">
              <div className="card-header">
                <h2 className="font-semibold text-gray-900">
                  {FLOWER_LABELS[selectedSegment.flowerKey]} Segment
                </h2>
                {selectedSegment.bouquetKey && (
                  <p className="text-sm text-gray-500">
                    Part of {BOUQUET_LABELS[selectedSegment.bouquetKey]}
                  </p>
                )}
              </div>
              <div className="card-body space-y-6">
                {/* Stage Position Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Stage Position
                  </label>
                  <StagePositionSelector
                    value={selectedSegment.stagePosition}
                    onChange={(pos) => handleStagePositionChange(selectedSegment.id, pos)}
                    isOnStage={currentBlueprint?.isOnStage ?? false}
                  />
                </div>

                {/* Attached Petals */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Attached Petals ({selectedSegment.petals.length})
                  </label>
                  {selectedSegment.petals.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">
                      No petals attached. Select petals from the right panel.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {selectedSegment.petals
                        .sort((a, b) => a.orderIndex - b.orderIndex)
                        .map((petal) => (
                          <div
                            key={petal.id}
                            className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                          >
                            <div>
                              <p className="text-sm font-medium text-gray-900">{petal.title}</p>
                              <p className="text-xs text-gray-500">
                                {PETAL_TYPE_LABELS[petal.petalType]}
                              </p>
                            </div>
                            <button
                              onClick={() => handleTogglePetal(petal.id)}
                              className="text-gray-400 hover:text-red-500"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                    </div>
                  )}
                </div>

                {/* Segment Notes */}
                {selectedSegment.notes && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <p className="text-sm text-gray-600">{selectedSegment.notes}</p>
                  </div>
                )}
              </div>
            </div>
          ) : currentBlueprint ? (
            <div className="card">
              <div className="card-body text-center py-12">
                <div className="text-5xl mb-4">🎯</div>
                <p className="text-gray-700 font-medium">Select a segment</p>
                <p className="text-sm text-gray-500 mt-1">
                  Choose a segment from the list to view details and attach petals
                </p>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-body text-center py-12">
                <div className="text-5xl mb-4">📋</div>
                <p className="text-gray-700 font-medium">No blueprint selected</p>
                <p className="text-sm text-gray-500 mt-1">
                  Select or create a blueprint to get started
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Right column - Petal picker */}
        <div className="lg:col-span-4">
          <div className="card">
            <div className="card-header">
              <h2 className="font-semibold text-gray-900">Petal Library</h2>
              <p className="text-sm text-gray-500">
                {selectedSegment ? 'Click to attach petals to segment' : 'Select a segment first'}
              </p>
            </div>
            <div className="card-body">
              <PetalPicker
                petals={petals}
                selectedPetalIds={selectedSegment?.petals.map((p) => p.id) ?? []}
                flowerFilter={selectedSegment?.flowerKey}
                onToggle={handleTogglePetal}
                disabled={!selectedSegment}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Create Blueprint Modal */}
      {showCreateBlueprint && (
        <CreateBlueprintModal
          onSubmit={handleCreateBlueprint}
          onClose={() => setShowCreateBlueprint(false)}
          isSubmitting={isCreating}
        />
      )}

      {/* Add Segment Modal */}
      {showAddSegment && blueprintId && (
        <AddSegmentModal
          onSubmit={handleAddSegment}
          onClose={() => setShowAddSegment(false)}
        />
      )}
    </div>
  );
}

// ============================================
// Blueprints List Component
// ============================================

interface BlueprintsListProps {
  blueprints: Blueprint[];
  selectedId?: string;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  isLoading?: boolean;
}

function BlueprintsList({ blueprints, selectedId, onSelect, onDelete, isLoading }: BlueprintsListProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (blueprints.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        <div className="text-3xl mb-2">📋</div>
        <p className="text-sm">No blueprints yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {blueprints.map((bp) => (
        <div
          key={bp.id}
          onClick={() => onSelect(bp.id)}
          className={clsx(
            'group flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all',
            selectedId === bp.id
              ? 'bg-primary-50 ring-1 ring-primary-300'
              : 'hover:bg-gray-50'
          )}
        >
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{bp.title}</p>
            <p className="text-xs text-gray-500">
              {EVENT_TYPE_LABELS[bp.eventType]} · {bp.segments.length} segments
            </p>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(bp.id);
            }}
            className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
}

// ============================================
// Segments List Component
// ============================================

interface SegmentsListProps {
  segments: BlueprintSegment[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onMove: (id: string, direction: 'up' | 'down') => void;
  isLoading?: boolean;
}

function SegmentsList({ segments, selectedId, onSelect, onDelete, onMove, isLoading }: SegmentsListProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (segments.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        <div className="text-3xl mb-2">🎯</div>
        <p className="text-sm">No segments yet. Add one!</p>
      </div>
    );
  }

  const sortedSegments = [...segments].sort((a, b) => a.orderIndex - b.orderIndex);

  return (
    <div className="space-y-2">
      {sortedSegments.map((segment, idx) => (
        <div
          key={segment.id}
          onClick={() => onSelect(segment.id)}
          className={clsx(
            'group flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all',
            selectedId === segment.id
              ? 'bg-primary-50 ring-1 ring-primary-300'
              : 'hover:bg-gray-50'
          )}
        >
          {/* Order indicator */}
          <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
            {idx + 1}
          </span>

          {/* Segment info */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {FLOWER_LABELS[segment.flowerKey]}
            </p>
            <p className="text-xs text-gray-500 flex items-center gap-1">
              {STAGE_POSITION_ICONS[segment.stagePosition]}{' '}
              {STAGE_POSITION_LABELS[segment.stagePosition]}
              {segment.petals.length > 0 && ` · ${segment.petals.length} petals`}
            </p>
          </div>

          {/* Move/Delete buttons */}
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMove(segment.id, 'up');
              }}
              disabled={idx === 0}
              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMove(segment.id, 'down');
              }}
              disabled={idx === sortedSegments.length - 1}
              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(segment.id);
              }}
              className="p-1 text-gray-400 hover:text-red-500"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================
// Stage Position Selector Component
// ============================================

interface StagePositionSelectorProps {
  value: StagePosition;
  onChange: (position: StagePosition) => void;
  isOnStage: boolean;
}

function StagePositionSelector({ value, onChange, isOnStage }: StagePositionSelectorProps) {
  if (!isOnStage) {
    return (
      <div className="text-sm text-gray-500 bg-gray-50 p-4 rounded-lg text-center">
        Stage positions are only available for on-stage presentations.
        <br />
        Edit the blueprint to enable "On Stage" mode.
      </div>
    );
  }

  // V-format stage layout
  const positions: { key: StagePosition; gridArea: string }[] = [
    { key: 'stage_left', gridArea: '2 / 1 / 3 / 2' },
    { key: 'stage_center_back', gridArea: '1 / 2 / 2 / 3' },
    { key: 'stage_right', gridArea: '2 / 3 / 3 / 4' },
    { key: 'stage_center_front', gridArea: '3 / 2 / 4 / 3' },
  ];

  return (
    <div className="relative">
      {/* Stage visual */}
      <div
        className="grid gap-2 p-4 bg-gray-100 rounded-lg"
        style={{
          gridTemplateColumns: '1fr 1fr 1fr',
          gridTemplateRows: '1fr 1fr 1fr',
        }}
      >
        {/* Audience label */}
        <div className="col-span-3 text-center text-xs text-gray-400 uppercase tracking-wide mb-2">
          Audience
        </div>

        {positions.map(({ key, gridArea }) => (
          <button
            key={key}
            onClick={() => onChange(key)}
            style={{ gridArea }}
            className={clsx(
              'flex flex-col items-center justify-center p-3 rounded-lg transition-all',
              value === key
                ? 'bg-primary-500 text-white ring-2 ring-primary-300'
                : 'bg-white text-gray-700 hover:bg-primary-50'
            )}
          >
            <span className="text-xl mb-1">{STAGE_POSITION_ICONS[key]}</span>
            <span className="text-xs font-medium">{STAGE_POSITION_LABELS[key]}</span>
          </button>
        ))}

        {/* Off-stage option */}
        <div className="col-span-3 mt-2">
          <button
            onClick={() => onChange('off_stage')}
            className={clsx(
              'w-full flex items-center justify-center gap-2 p-2 rounded-lg transition-all',
              value === 'off_stage'
                ? 'bg-gray-700 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-200'
            )}
          >
            <span>{STAGE_POSITION_ICONS.off_stage}</span>
            <span className="text-sm">Off Stage</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Petal Picker Component
// ============================================

interface PetalPickerProps {
  petals: PetalDto[];
  selectedPetalIds: string[];
  flowerFilter?: FlowerKey;
  onToggle: (petalId: string) => void;
  disabled?: boolean;
}

function PetalPicker({ petals, selectedPetalIds, flowerFilter, onToggle, disabled }: PetalPickerProps) {
  const [filterFlower, setFilterFlower] = useState<FlowerKey | 'all'>(flowerFilter || 'all');
  const [searchTerm, setSearchTerm] = useState('');

  // Update filter when segment changes
  useEffect(() => {
    if (flowerFilter) {
      setFilterFlower(flowerFilter);
    }
  }, [flowerFilter]);

  // Filter petals
  const filteredPetals = petals.filter((petal) => {
    if (filterFlower !== 'all' && !petal.flowerKeys.includes(filterFlower)) {
      return false;
    }
    if (searchTerm && !petal.title.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    return true;
  });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-2">
        <select
          value={filterFlower}
          onChange={(e) => setFilterFlower(e.target.value as FlowerKey | 'all')}
          className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
        >
          <option value="all">All Flowers</option>
          {Object.entries(FLOWER_LABELS).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search..."
          className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
        />
      </div>

      {/* Petals list */}
      {filteredPetals.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <div className="text-3xl mb-2">🌸</div>
          <p className="text-sm">No petals found</p>
        </div>
      ) : (
        <div className="max-h-96 overflow-y-auto space-y-2">
          {filteredPetals.map((petal) => {
            const isSelected = selectedPetalIds.includes(petal.id);
            return (
              <div
                key={petal.id}
                onClick={() => !disabled && onToggle(petal.id)}
                className={clsx(
                  'flex items-center gap-3 p-3 rounded-lg border transition-all',
                  disabled
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer',
                  isSelected
                    ? 'bg-primary-50 border-primary-300'
                    : 'bg-white border-gray-200 hover:border-gray-300'
                )}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => {}}
                  disabled={disabled}
                  className="w-4 h-4 text-primary-600 rounded"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{petal.title}</p>
                  <p className="text-xs text-gray-500">
                    {PETAL_TYPE_LABELS[petal.petalType]}
                    {petal.flowerKeys.length > 0 && (
                      <> · {petal.flowerKeys.map((k) => FLOWER_LABELS[k]).join(', ')}</>
                    )}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================
// Create Blueprint Modal
// ============================================

interface CreateBlueprintModalProps {
  onSubmit: (data: CreateBlueprintRequest) => void;
  onClose: () => void;
  isSubmitting?: boolean;
}

function CreateBlueprintModal({ onSubmit, onClose, isSubmitting }: CreateBlueprintModalProps) {
  const [formData, setFormData] = useState<CreateBlueprintRequest>({
    title: '',
    eventType: 'keynote',
    goal: 'build_authority',
    domainKey: 'general',
    isOnStage: true,
    useDefaultTemplate: true,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) return;
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Create Blueprint</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., Q1 Investor Pitch"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              required
              autoFocus
            />
          </div>

          {/* Event Type & Goal */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
              <select
                value={formData.eventType}
                onChange={(e) => setFormData((prev) => ({ ...prev, eventType: e.target.value as EventType }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                {Object.entries(EVENT_TYPE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Goal</label>
              <select
                value={formData.goal}
                onChange={(e) => setFormData((prev) => ({ ...prev, goal: e.target.value as BlueprintGoal }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                {Object.entries(GOAL_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* On Stage toggle */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="isOnStage"
              checked={formData.isOnStage}
              onChange={(e) => setFormData((prev) => ({ ...prev, isOnStage: e.target.checked }))}
              className="w-4 h-4 text-primary-600 rounded"
            />
            <label htmlFor="isOnStage" className="text-sm text-gray-700">
              On-stage presentation (enables stage movement guidance)
            </label>
          </div>

          {/* Default template toggle */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="useDefaultTemplate"
              checked={formData.useDefaultTemplate}
              onChange={(e) => setFormData((prev) => ({ ...prev, useDefaultTemplate: e.target.checked }))}
              className="w-4 h-4 text-primary-600 rounded"
            />
            <label htmlFor="useDefaultTemplate" className="text-sm text-gray-700">
              Start with default segments template
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !formData.title.trim()}
              className={clsx(
                'px-6 py-2 text-sm font-medium rounded-lg transition-all',
                isSubmitting || !formData.title.trim()
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-primary-600 text-white hover:bg-primary-700'
              )}
            >
              {isSubmitting ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================
// Add Segment Modal
// ============================================

interface AddSegmentModalProps {
  onSubmit: (data: CreateSegmentRequest) => void;
  onClose: () => void;
}

function AddSegmentModal({ onSubmit, onClose }: AddSegmentModalProps) {
  const [formData, setFormData] = useState<CreateSegmentRequest>({
    flowerKey: 'problem',
    stagePosition: 'stage_left',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Add Segment</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Flower Key */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Flower</label>
            <select
              value={formData.flowerKey}
              onChange={(e) => setFormData((prev) => ({ ...prev, flowerKey: e.target.value as FlowerKey }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              {Object.entries(FLOWER_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>

          {/* Stage Position */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Stage Position</label>
            <select
              value={formData.stagePosition}
              onChange={(e) => setFormData((prev) => ({ ...prev, stagePosition: e.target.value as StagePosition }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              {Object.entries(STAGE_POSITION_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Add
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default BlueprintEditorPage;
